import { MEMBER_OPTION_PERMISSIONS, OVERVIEW_PERMISSIONS, Permission, UPLOAD_PERMISSIONS, hasAnyPermission, } from './permissions';
/**
 * 每个接口要什么权限 —— **全项目只有这一份**（CR-040）。
 *
 * 后端 controller 上写 `@Endpoint('GET sites/:siteId/hidden-works')`，装饰器从这里查权限；
 * 前端路由表（`apps/web/src/lib/routes/registry.ts`）声明「这一页加载时调哪些接口」，
 * 也引用这里的 key。改之前两边各写各的：接口加了门槛、页面还照调不误，
 * 结果是某些权限组合一打开页面就 403 —— 只有甲方这种新角色才会撞上。
 *
 * key 的格式和 `route-permissions.test.ts` 拼出来的一样：`动词 前缀/路径`。
 * 值：
 * - 权限数组：任一满足即可（依赖由 `hasPermission` 兜底）；
 * - `'login'`：只要登录。每一条都要能说出为什么（写在旁边）；
 * - `'public'`：不用登录。
 */
export const API_PERMISSIONS = {
    // ---- 认证 ----
    'POST auth/login': 'public',
    'POST auth/refresh': 'public',
    'POST auth/logout': 'public',
    'POST auth/change-password': 'login', // 自己的密码
    'GET auth/me': 'login', // 自己的账号
    'POST auth/device': 'login', // 自己的设备
    'PATCH me/preferences': 'login', // 自己的偏好
    'GET health': 'public',
    // ---- 账号 · 角色 ----
    'GET users': [Permission.USER_MANAGE, Permission.SITE_MANAGE], // 工地设置加成员要选人
    'GET users/:id': [Permission.USER_MANAGE, Permission.SITE_MANAGE],
    'POST users': [Permission.USER_MANAGE],
    'PATCH users/:id': [Permission.USER_MANAGE],
    'POST users/:id/reset-password': [Permission.USER_MANAGE],
    'POST users/:id/set-password': [Permission.USER_MANAGE], // 直接写入指定密码（CR-077），和重置同一套门槛
    // 用户页里直接勾这个人所在的工地（CR-071）：动的是成员关系，门槛和工地设置里加人一样
    'GET users/:id/sites': [Permission.SITE_MANAGE],
    'PUT users/:id/sites': [Permission.SITE_MANAGE],
    'GET roles': [Permission.USER_MANAGE, Permission.SITE_MANAGE, Permission.ROLE_MANAGE],
    // 共享角色只有超管能动；单位自建的按 canEditRole（role.manage + 子集，CR-073）—— 都在 RolesService 里判
    'POST roles': [Permission.ROLE_MANAGE],
    'PATCH roles/:id': [Permission.ROLE_MANAGE],
    'DELETE roles/:id': [Permission.ROLE_MANAGE],
    // ---- 单位（CR-073：公司 / 分公司）----
    // 建人 / 建角色 / 建工地的下拉和筛选要用。平台账号拿全部，单位账号只回自己那一家（OrgsService 里按范围过滤）
    'GET orgs': 'login',
    'POST orgs': [Permission.USER_MANAGE], // 另判 isSystem：只有超管能开新单位
    'PATCH orgs/:id': [Permission.USER_MANAGE], // 同上：改名、归档
    'GET audit-logs': [Permission.AUDIT_VIEW],
    'GET audit-logs/actions': [Permission.AUDIT_VIEW],
    'GET audit-logs/actors': [Permission.AUDIT_VIEW],
    // ---- 工地 ----
    'GET sites': 'login', // 列表本身按 SiteMember / site.viewAll 过滤
    'GET sites/:id': 'login', // @SiteParam 挡成员关系
    'POST sites': [Permission.SITE_CREATE],
    'PATCH sites/:id': [Permission.SITE_MANAGE],
    'GET sites/:siteId/members': [Permission.SITE_MANAGE],
    'POST sites/:siteId/members': [Permission.SITE_MANAGE],
    'DELETE sites/:siteId/members/:userId': [Permission.SITE_MANAGE],
    'GET sites/:siteId/member-options': MEMBER_OPTION_PERMISSIONS,
    'GET sites/:siteId/overview': OVERVIEW_PERMISSIONS,
    'GET geocode/search': [Permission.SITE_CREATE, Permission.SITE_MANAGE],
    'GET geocode/reverse': [Permission.SITE_CREATE, Permission.SITE_MANAGE],
    // ---- 图纸 · 进度表（同一张表，kind 级别的细分见 API_SUB_PERMISSIONS）----
    'GET sites/:siteId/drawings': [Permission.DRAWING_VIEW, Permission.SCHEDULE_VIEW],
    'POST sites/:siteId/drawings': [Permission.DRAWING_UPLOAD, Permission.SCHEDULE_UPLOAD],
    'GET drawings/:id': [Permission.DRAWING_VIEW, Permission.SCHEDULE_VIEW],
    'GET drawings/:id/versions': [Permission.DRAWING_VIEW, Permission.SCHEDULE_VIEW],
    // 进度表的结构化数据（CR-080）：甘特图和给 AI / 脚本的 JSON 是同一份。草稿只回给能换进度表的人（service 里判）
    'GET sites/:siteId/schedule': [Permission.SCHEDULE_VIEW],
    'PUT sites/:siteId/schedule': [Permission.SCHEDULE_UPLOAD],
    'POST drawings/:id/pins': [Permission.DRAWING_UPLOAD],
    'DELETE drawings/pins/:pinId': [Permission.DRAWING_UPLOAD],
    // ---- 日报 ----
    'POST daily-logs': [Permission.DAILY_LOG_WRITE],
    'GET me/daily-logs': [Permission.DAILY_LOG_WRITE],
    'GET sites/:siteId/daily-logs': [Permission.DAILY_LOG_VIEW],
    // ---- 隐蔽工程 ----
    'POST hidden-works': [Permission.HIDDEN_WORK_WRITE],
    'GET me/hidden-works': [Permission.HIDDEN_WORK_WRITE], // 拍的人永远能看自己拍的
    'GET sites/:siteId/hidden-works': [Permission.HIDDEN_WORK_VIEW],
    'GET sites/:siteId/hidden-works/categories': [Permission.HIDDEN_WORK_WRITE, Permission.HIDDEN_WORK_VIEW],
    'GET hidden-works/:id': [Permission.HIDDEN_WORK_VIEW, Permission.HIDDEN_WORK_WRITE],
    // ---- 采购 ----
    'POST purchases': [Permission.PURCHASE_WRITE],
    'GET me/purchases': [Permission.PURCHASE_WRITE],
    'GET sites/:siteId/purchases': [Permission.PURCHASE_VIEW],
    'GET sites/:siteId/purchases/summary': [Permission.PURCHASE_VIEW],
    'GET sites/:siteId/purchases/categories': [Permission.PURCHASE_WRITE, Permission.PURCHASE_VIEW],
    'POST purchases/:id/review': [Permission.PURCHASE_REVIEW],
    'GET sites/:siteId/exports/purchases': [Permission.PURCHASE_EXPORT], // CR-043 之前挂在 purchase.view
    // ---- 下载（CR-043）：表格 + 附件打包 zip，考勤只有表格 ----
    'GET sites/:siteId/exports/daily-logs': [Permission.DAILY_LOG_EXPORT],
    'GET sites/:siteId/exports/hidden-works': [Permission.HIDDEN_WORK_EXPORT],
    'GET sites/:siteId/exports/attendance': [Permission.ATTENDANCE_EXPORT],
    'GET sites/:siteId/exports/drawings': [Permission.DRAWING_EXPORT],
    'GET sites/:siteId/exports/schedule': [Permission.SCHEDULE_EXPORT],
    'GET drawings/:id/download': [Permission.DRAWING_EXPORT, Permission.SCHEDULE_EXPORT], // 按 kind 再对
    // ---- 考勤 ----
    'POST punches': [Permission.ATTENDANCE_PUNCH],
    'GET me/shift': 'login', // 工地锁定要读；打不打卡的人都要知道自己锁在哪
    'GET sites/:siteId/me/punches/today': 'login', // 同上，只回本人的
    'GET sites/:siteId/punches': [Permission.ATTENDANCE_VIEW],
    'GET sites/:siteId/attendance/summary': [Permission.ATTENDANCE_VIEW],
    // 待复核 + 异常，不限日期（CR-052）
    'GET sites/:siteId/attendance/exceptions': [Permission.ATTENDANCE_VIEW],
    // 补卡表单的建议（他平时几点、这天有哪些卡）：只有能补录的人用
    'GET sites/:siteId/attendance/backfill-context': [Permission.ATTENDANCE_MANAGE],
    'POST punches/:id/review': [Permission.ATTENDANCE_MANAGE],
    'POST punches/manual': [Permission.ATTENDANCE_MANAGE],
    // 异常标记已处理 / 撤销（CR-052）：和补录同一个门槛
    'POST punches/:id/anomaly-resolution': [Permission.ATTENDANCE_MANAGE],
    'DELETE punches/:id/anomaly-resolution': [Permission.ATTENDANCE_MANAGE],
    // ---- 文件 ----
    'POST files/presign': UPLOAD_PERMISSIONS,
    'POST files/commit': UPLOAD_PERMISSIONS,
};
/**
 * 比接口守卫更细的一层：图纸和进度表共用一张表，权限分开勾。
 * 守卫只能收「两种任一」，具体是哪种由 `drawings.service` 按 kind 再对一次 ——
 * 用的就是这里的规则。
 */
export const API_SUB_PERMISSIONS = {
    'GET sites/:siteId/drawings?kind=DRAWING': [Permission.DRAWING_VIEW],
    'GET sites/:siteId/drawings?kind=SCHEDULE': [Permission.SCHEDULE_VIEW],
    'POST sites/:siteId/drawings?kind=DRAWING': [Permission.DRAWING_UPLOAD],
    'POST sites/:siteId/drawings?kind=SCHEDULE': [Permission.SCHEDULE_UPLOAD],
    'GET drawings/:id/download?kind=DRAWING': [Permission.DRAWING_EXPORT],
    'GET drawings/:id/download?kind=SCHEDULE': [Permission.SCHEDULE_EXPORT],
};
export function apiRule(key) {
    return key in API_SUB_PERMISSIONS
        ? API_SUB_PERMISSIONS[key]
        : API_PERMISSIONS[key];
}
/** 这个角色调这个接口会不会被权限挡（工地成员关系另算） */
export function apiAllowed(role, key) {
    const rule = apiRule(key);
    if (rule === 'public')
        return true;
    if (rule === 'login')
        return Boolean(role);
    return hasAnyPermission(role, rule);
}
const MATCHERS = Object.keys(API_PERMISSIONS).map((key) => {
    const [method, template] = key.split(' ');
    const pattern = template
        .split('/')
        .map((seg) => (seg.startsWith(':') ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
        .join('/');
    return { key, method, re: new RegExp(`^${pattern}$`) };
});
/**
 * 把一次真实请求对到表里的 key。前端开发环境用它检查「这一页调了自己没权限的接口」。
 * `path` 不带前缀 `/api/v1`、不带查询串，前后的斜杠无所谓。
 * 固定路径优先于带参数的（`me/hidden-works` 不会被 `hidden-works/:id` 吃掉）。
 */
export function matchEndpoint(method, path) {
    const clean = path.replace(/^\/+|\/+$/g, '').split('?')[0] ?? '';
    const upper = method.toUpperCase();
    let best = null;
    for (const m of MATCHERS) {
        if (m.method !== upper || !m.re.test(clean))
            continue;
        const params = (m.key.match(/:/g) ?? []).length;
        if (!best || params < best.params)
            best = { key: m.key, params };
    }
    return best?.key ?? null;
}
//# sourceMappingURL=api-permissions.js.map